import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";

import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { contactUsCallback } from "@helpers/helpers";

import {
  CreateMergeLinkTokenMutation,
  CreateMergeLinkTokenMutationVariables,
  GetAvailableMergeIntegrationsQuery,
} from "../../../../types/graphql-schema";
import createMergeLinkTokenMutation from "../../graphql/create-merge-link-token";
import getAvailableMergeIntegrationsQuery from "../../graphql/get-available-merge-integrations";
import IntegrationCard from "./integrations-card";
import {
  ALL_INTEGRATIONS_CATEGORY,
  MERGE_CATEGORY_DESCRIPTIONS,
  getIntegrationCategoryLabel,
  isVisibleIntegrationCategory,
  normalizeIntegrationCategory,
  requiresManualSetupIntegration,
  sortIntegrationCategories,
} from "./integrations-constants";
import {
  AvailableMergeIntegration,
  MergeIntegration,
} from "./integrations-types";
import { useMergeIntegrationLink } from "./use-merge-integration-link";

const IntegrationsMarketMerge = ({
  organization: _organization,
  connectedMergeIntegrations,
  activeCategory,
  searchQuery,
  onRefetch,
  onMarketplaceDataChange,
}: {
  organization: any;
  connectedMergeIntegrations: MergeIntegration[];
  activeCategory: string;
  searchQuery: string;
  onRefetch: () => void;
  onMarketplaceDataChange: (data: {
    categories: readonly string[];
    ready: boolean;
    visibleCount: number;
  }) => void;
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [connectingIntegration, setConnectingIntegration] = useState<{
    slug: string;
    category: string;
  } | null>(null);
  const [manualSetupIntegrationName, setManualSetupIntegrationName] = useState<
    string | null
  >(null);

  // ---------------------------------------------------------------------------
  // Queries and mutations
  // ---------------------------------------------------------------------------
  const {
    data: availableMergeIntegrationsData,
    loading: availableMergeIntegrationsLoading,
  } = useQuery<GetAvailableMergeIntegrationsQuery>(
    getAvailableMergeIntegrationsQuery,
    {
      skip: !waffle.flag_is_active("merge_dev_enabled"),
    },
  );

  const [createMergeLinkToken, { loading: mergeLinkTokenLoading }] =
    useMutation<
      CreateMergeLinkTokenMutation,
      CreateMergeLinkTokenMutationVariables
    >(createMergeLinkTokenMutation);

  // ---------------------------------------------------------------------------
  // Merge.dev link flow
  // ---------------------------------------------------------------------------
  const { startLink } = useMergeIntegrationLink({
    onSuccess: () => {
      setConnectingIntegration(null);
      onRefetch();
    },
    successMessage: "Integration connected successfully",
  });

  const handleConnectMergeIntegration = useCallback(
    async (integration: AvailableMergeIntegration, category: string) => {
      if (requiresManualSetupIntegration(integration.name)) {
        setManualSetupIntegrationName(integration.name);
        return;
      }

      setConnectingIntegration({ slug: integration.slug, category });
      try {
        const result = await createMergeLinkToken({
          variables: {
            integrationSlug: integration.slug,
            category,
          },
        });

        if (result.data?.createMergeLinkToken?.success) {
          const linkToken = result.data.createMergeLinkToken.linkToken;
          const originId = result.data.createMergeLinkToken.endUserOriginId;
          if (linkToken && originId) {
            startLink(linkToken, originId);
          }
        } else {
          errorNotificationVar({
            title: "Error",
            description:
              result.data?.createMergeLinkToken?.error ||
              "Failed to create link token",
          });
          setConnectingIntegration(null);
        }
      } catch (error) {
        errorNotificationVar({
          title: "Error",
          description: "Failed to create link token",
        });
        setConnectingIntegration(null);
        throw error;
      }
    },
    [createMergeLinkToken, startLink],
  );

  // ---------------------------------------------------------------------------
  // Build integration-category pairs (exclude already connected)
  // ---------------------------------------------------------------------------
  const connectedKeys = useMemo(
    () =>
      new Set(connectedMergeIntegrations.map((i) => `${i.slug}_${i.category}`)),
    [connectedMergeIntegrations],
  );

  const availableMergeIntegrationPairs = useMemo(() => {
    const pairs: Array<{
      integration: AvailableMergeIntegration;
      category: string;
    }> = [];

    (availableMergeIntegrationsData?.availableMergeIntegrations ?? [])
      .filter((i): i is NonNullable<typeof i> => i !== null)
      .forEach((integration) => {
        // Create a card for each category the integration supports
        integration.categories
          .filter((c): c is string => c !== null)
          .filter(isVisibleIntegrationCategory)
          .forEach((category) => {
            const key = `${integration.slug}_${category}`;
            if (!connectedKeys.has(key)) {
              pairs.push({ integration, category });
            }
          });
      });

    return pairs;
  }, [availableMergeIntegrationsData, connectedKeys]);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const marketplaceCategories = useMemo(
    () =>
      sortIntegrationCategories(
        availableMergeIntegrationPairs.map(({ category }) => category),
      ),
    [availableMergeIntegrationPairs],
  );

  const visibleMergeIntegrationPairs = useMemo(
    () =>
      availableMergeIntegrationPairs.filter(({ integration, category }) => {
        const normalizedCategory = normalizeIntegrationCategory(category);
        const categoryLabel = getIntegrationCategoryLabel(category);
        const matchesSearch =
          !normalizedSearchQuery ||
          [
            integration.name,
            integration.slug,
            normalizedCategory,
            categoryLabel,
          ].some((value) =>
            value.toLowerCase().includes(normalizedSearchQuery),
          );
        const matchesCategory =
          normalizedSearchQuery ||
          activeCategory === ALL_INTEGRATIONS_CATEGORY ||
          normalizedCategory === activeCategory;

        return matchesSearch && matchesCategory;
      }),
    [activeCategory, availableMergeIntegrationPairs, normalizedSearchQuery],
  );

  useEffect(() => {
    onMarketplaceDataChange({
      categories: marketplaceCategories,
      ready: !availableMergeIntegrationsLoading,
      visibleCount: visibleMergeIntegrationPairs.length,
    });
  }, [
    availableMergeIntegrationsLoading,
    marketplaceCategories,
    onMarketplaceDataChange,
    visibleMergeIntegrationPairs.length,
  ]);

  // Early return after all hooks are called
  if (!waffle.flag_is_active("merge_dev_enabled")) {
    return null;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {manualSetupIntegrationName && (
        <Modal
          open
          onClose={() => setManualSetupIntegrationName(null)}
          dialogClassName="max-w-lg"
        >
          <div className="p-6 space-y-5">
            <ModalTitle onClose={() => setManualSetupIntegrationName(null)}>
              Contact us to enable {manualSetupIntegrationName}
            </ModalTitle>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                This integration requires a short setup step from Topicflow
                before it can be connected to your organization.
              </p>
              <p>
                Email{" "}
                <a
                  href={`mailto:support@topicflow.com?subject=${encodeURIComponent(
                    `Enable ${manualSetupIntegrationName} integration`,
                  )}`}
                  className="text-blue-600 hover:underline"
                >
                  support@topicflow.com
                </a>{" "}
                or send us a message and we will help get it enabled.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                theme={buttonTheme.lightGray}
                size={ButtonSize.large}
                onClick={() => setManualSetupIntegrationName(null)}
              >
                Close
              </Button>
              <Button
                theme={buttonTheme.primary}
                size={ButtonSize.large}
                onClick={() => {
                  contactUsCallback();
                  setManualSetupIntegrationName(null);
                }}
              >
                Message us
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {visibleMergeIntegrationPairs.map(({ integration, category }) => {
        const key = `${integration.slug}_${category}`;
        const categoryDescription =
          MERGE_CATEGORY_DESCRIPTIONS[normalizeIntegrationCategory(category)] ||
          "";
        return (
          <IntegrationCard
            key={key}
            name={integration.name}
            category={getIntegrationCategoryLabel(category)}
            logo={integration.squareImage || integration.image || ""}
            description={
              <>
                <ul className="list-disc ml-4 py-2 italic">
                  <li>{`Connect your ${integration.name} account`}</li>
                  {categoryDescription && <li>{categoryDescription}</li>}
                </ul>
              </>
            }
            isLoading={
              mergeLinkTokenLoading &&
              connectingIntegration?.slug === integration.slug &&
              connectingIntegration?.category === category
            }
            onConnect={() => {
              handleConnectMergeIntegration(integration, category);
            }}
          />
        );
      })}
    </>
  );
};

export { IntegrationsMarketMerge };
